import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from "react";
import axiosClient from "../axiosClient";
import { ACCESS_TOKEN_KEY, platform } from "../brand";
import { marketingUrl } from "../lib/marketingUrls";
import {
  EMPTY_PLATFORM_SUMMARY,
  PlatformSummaryResponse,
} from "../constants/dashboardStats";
import { Class, EnrolmentAccessStatus, Subscription } from "../types";
import toast from "react-hot-toast";
import {
  deriveAccessStatusFromEnrolments,
  normalizeAccessStatus,
  registerAccessExpiredHandler,
} from "../lib/accessExpired";
import { sortEnrolments } from "../utils/sortEnrolments";

// TODO: find a way to share these types between frontend and backend
interface Student {
  id: number,
  userId: number,
  formName: string,
  yearGroup: number
}

interface Teacher {
  id: number,
}

interface User {
  id: number;
  title: string;
  firstName: string;
  lastName: string;
  username: string;
  type: 'student' | 'teacher';
  student: Student;
  teacher: Teacher;
  subscription: Subscription;
}

export type LoginResult = {
  success: boolean;
  accessStatus: EnrolmentAccessStatus;
};

export type AuthValidationResult = {
  valid: boolean;
  accessStatus: EnrolmentAccessStatus;
};

interface UserContextType {
  user: User | null;
  accessStatus: EnrolmentAccessStatus;
  login: (username: string, password: string) => Promise<LoginResult>;
  logout: () => void;
  validateCookie: () => Promise<AuthValidationResult>;
  enrolments: Class[];
  fetchEnrolments: () => Promise<void>;
  platformSummary: PlatformSummaryResponse | null;
  fetchPlatformSummary: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode; // Specify ReactNode type for children prop
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessStatus, setAccessStatus] = useState<EnrolmentAccessStatus>("none");
  const [enrolments, setEnrolments] = useState<Class[]>([]);
  const [platformSummary, setPlatformSummary] = useState<PlatformSummaryResponse | null>(null);

  useEffect(() => {
    registerAccessExpiredHandler(() => {
      setAccessStatus("expired");
    });
  }, []);

  const login = async (username: string, password: string): Promise<LoginResult> => {
    try {
      const loginData: {
        isFirstTimeLogin: boolean;
        user: User;
        accessToken: string;
        refreshToken: string;
        accessStatus?: EnrolmentAccessStatus;
      } = (
        await axiosClient.post("/auth/login", {
          username,
          password,
          platform,
        })
      ).data;
      const nextAccessStatus = normalizeAccessStatus(loginData.accessStatus);
      setUser(loginData.user);
      setAccessStatus(nextAccessStatus);
      sessionStorage.setItem(ACCESS_TOKEN_KEY, loginData.accessToken);
      return { success: true, accessStatus: nextAccessStatus };
    } catch (err) {
      return { success: false, accessStatus: "none" };
    }
  };

  const logout = async () => {
    try {
      await axiosClient.post("/auth/logout");
      sessionStorage.removeItem(ACCESS_TOKEN_KEY);
      window.location.assign(marketingUrl("/"));
    } catch (err) {
      console.log(err);
      toast.error("Logout failed");
    }
    setUser(null);
    setAccessStatus("none");
    setPlatformSummary(null);
  };

  const validateCookie = async (): Promise<AuthValidationResult> => {
    try {
      const validCookie: {
        status: string,
        user: User,
        accessStatus?: EnrolmentAccessStatus,
      } = (await axiosClient.get("/auth/check-cookie")).data;
      const nextAccessStatus = normalizeAccessStatus(validCookie.accessStatus);
      setUser(validCookie.user);
      setAccessStatus(nextAccessStatus);
      return { valid: true, accessStatus: nextAccessStatus };
    } catch (err) {
      console.error(err);
      setUser(null);
      setAccessStatus("none");
      return { valid: false, accessStatus: "none" };
    }
  };

  const fetchEnrolments = async () => {

    const studentView = user?.type === 'student'
    if (studentView) {
      // refresh enrolments every time you load dashboard, but keep it in state in case sidebar needs it (multiple places across app)
      const enrolmentsResponse: Class[] = (
        await axiosClient.get(`/students/${user.id}/enrolments`)
      ).data;
      const sortedEnrolments = sortEnrolments(enrolmentsResponse);
      setEnrolments(sortedEnrolments);
      const derivedStatus = deriveAccessStatusFromEnrolments(sortedEnrolments);
      if (derivedStatus) {
        setAccessStatus(derivedStatus);
      }
    } else {
      // perhaps these can be merged into one endpoint
      // only make callout if `enrolments` not currently stored in state
      const classesResponse: Class[] = (await axiosClient.get(`/teachers/${user?.teacher.id}/classes`)).data;
      setEnrolments(sortEnrolments(classesResponse));
    }
  }

  const fetchPlatformSummary = async () => {
    if (user?.type !== "student") {
      return;
    }

    try {
      const response = await axiosClient.get<PlatformSummaryResponse>("/platform-summary");
      setPlatformSummary(response.data);
    } catch (err) {
      console.error(err);
      setPlatformSummary(EMPTY_PLATFORM_SUMMARY);
    }
  };

  useEffect(() => {
    if (user?.type !== "student") {
      setPlatformSummary(null);
      return;
    }

    let cancelled = false;

    const loadPlatformSummary = async () => {
      try {
        const response = await axiosClient.get<PlatformSummaryResponse>("/platform-summary");
        if (!cancelled) {
          setPlatformSummary(response.data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          setPlatformSummary(EMPTY_PLATFORM_SUMMARY);
        }
      }
    };

    loadPlatformSummary();

    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.type]);

  return (
    <UserContext.Provider
      value={{
        user,
        accessStatus,
        login,
        logout,
        validateCookie,
        enrolments,
        fetchEnrolments,
        platformSummary,
        fetchPlatformSummary,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within an UserProvider");
  }
  return context;
};
